import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { Button, Field, FormActions, FormSection, InlineBanner, Input, LinkButton, notify, PageHeader, Select, Skeleton, SkeletonRegion, StateBlock } from '../../components/ui';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useApiError } from '../../hooks/useApiError';
import { usePageTitle } from '../../hooks/usePageTitle';
import api, { asPlanLimit } from '../../services/api';
import { getTranslation } from '../../utils/translation.utils';
import type { LocationRecord, MenuRecord } from './apiTypes';
import { useRequest } from './useRequest';

interface Values {
    restaurantId: string;
    name: string;
    address: string;
    phoneNumber: string;
    activeMenuId: string;
}

interface Loaded {
    location: LocationRecord | null;
    restaurants: { id: string; name: string }[];
    menus: MenuRecord[];
}

interface BodyProps extends Loaded {
    id: string | undefined;
    defaultRestaurantId: string;
    isAdmin: boolean;
}

const LocationFormBody = ({ id, location, restaurants, menus, defaultRestaurantId, isAdmin }: BodyProps) => {
    const { t, i18n } = useTranslation(['locations', 'common']);
    const navigate = useNavigate();
    const apiError = useApiError();
    const initial: Values = {
        restaurantId: location?.restaurantId ?? defaultRestaurantId,
        name: location?.name ?? '',
        address: location?.address ?? '',
        phoneNumber: location?.phoneNumber ?? '',
        activeMenuId: location?.activeMenuId ?? '',
    };
    const [values, setValues] = useState<Values>(initial);
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [failure, setFailure] = useState<{ message: string; planLimit: boolean } | null>(null);

    const set = <K extends keyof Values>(key: K, value: Values[K]) => setValues((v) => ({ ...v, [key]: value }));
    const dirty = JSON.stringify(values) !== JSON.stringify(initial);
    const errors = {
        name: submitted && !values.name.trim() ? t('form.required') : undefined,
        address: submitted && !values.address.trim() ? t('form.required') : undefined,
        restaurantId: submitted && !values.restaurantId ? t('form.required') : undefined,
    };

    const menuOptions = [
        { value: '', label: t('form.noMenu') },
        ...menus
            .filter((m) => m.restaurantId === undefined || m.restaurantId === values.restaurantId)
            .map((m) => ({ value: m.id, label: getTranslation(m.translations, 'name', m.name, i18n.language) })),
    ];

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
        if (!values.name.trim() || !values.address.trim() || !values.restaurantId) return;
        setSaving(true);
        setFailure(null);
        const body = {
            name: values.name.trim(),
            address: values.address.trim(),
            phoneNumber: values.phoneNumber.trim(),
            activeMenuId: values.activeMenuId || null,
        };
        try {
            if (id) await api.put(`/locations/${id}`, body);
            else await api.post('/locations', { ...body, restaurantId: values.restaurantId, activeMenuId: values.activeMenuId || undefined });
            notify.success(t(id ? 'form.updated' : 'form.created', { name: body.name }));
            navigate(id ? `/locations/${id}` : '/locations');
        } catch (err) {
            setFailure({ message: apiError(err), planLimit: asPlanLimit(err) !== null });
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} noValidate className="mx-auto flex max-w-3xl flex-col gap-6">
            {failure && (
                <InlineBanner tone="danger">
                    <p>{failure.message}</p>
                    {failure.planLimit && <p className="mt-1">{t('form.upgrade')}</p>}
                </InlineBanner>
            )}
            <FormSection title={t('form.details')}>
                {isAdmin && !id && (
                    <Field label={t('form.restaurant')} error={errors.restaurantId}>
                        {(c) => (
                            <Select
                                {...c}
                                invalid={Boolean(errors.restaurantId)}
                                value={values.restaurantId || null}
                                onChange={(v) => setValues((s) => ({ ...s, restaurantId: v, activeMenuId: '' }))}
                                options={restaurants.map((r) => ({ value: r.id, label: r.name }))}
                                placeholder={t('form.restaurantPlaceholder')}
                            />
                        )}
                    </Field>
                )}
                <Field label={t('form.name')} error={errors.name}>
                    {(c) => <Input {...c} value={values.name} onChange={(e) => set('name', e.target.value)} autoComplete="off" />}
                </Field>
                <Field label={t('form.address')} error={errors.address}>
                    {(c) => <Input {...c} value={values.address} onChange={(e) => set('address', e.target.value)} autoComplete="street-address" />}
                </Field>
                <Field label={t('form.phone')} optional>
                    {(c) => <Input {...c} type="tel" value={values.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} autoComplete="tel" />}
                </Field>
            </FormSection>
            <FormSection title={t('form.menu')} description={t('form.menuHint')}>
                <Field label={t('form.activeMenu')}>
                    {(c) => <Select {...c} value={values.activeMenuId} onChange={(v) => set('activeMenuId', v)} options={menuOptions} />}
                </Field>
            </FormSection>
            <FormActions
                onCancel={() => navigate(id ? `/locations/${id}` : '/locations')}
                dirty={dirty}
                submitting={saving}
                submitLabel={t(id ? 'form.save' : 'form.create')}
            />
        </form>
    );
};

const LocationForm = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation('locations');
    usePageTitle(t(id ? 'form.editTitle' : 'form.createTitle'));
    const { restaurantId, isAdmin } = useActiveLocation();
    const [params] = useSearchParams();
    const requestedRestaurant = isAdmin ? params.get('restaurantId') : null;

    const { data, state, missing, retry } = useRequest<Loaded>(`form:${id ?? 'new'}`, async () => {
        const [location, restaurants, menus] = await Promise.all([
            id ? api.get<LocationRecord>(`/locations/${id}`).then((r) => r.data) : Promise.resolve(null),
            isAdmin ? api.get<{ id: string; name: string }[]>('/restaurants').then((r) => r.data) : Promise.resolve([]),
            api.get<MenuRecord[]>('/menus').then((r) => r.data),
        ]);
        return { location, restaurants, menus };
    });

    return (
        <>
            <PageHeader
                title={t(id ? 'form.editTitle' : 'form.createTitle')}
                back={{ to: id ? `/locations/${id}` : '/locations', label: id ? t('detail.back') : t('list.title') }}
            />
            {state === 'error' &&
                (missing ? (
                    <StateBlock
                        variant="empty"
                        icon={MapPinIcon}
                        title={t('detail.notFound')}
                        body={t('detail.notFoundBody')}
                        action={
                            <LinkButton to="/locations" variant="outline">
                                {t('list.title')}
                            </LinkButton>
                        }
                    />
                ) : (
                    <StateBlock variant="error" title={t('form.loadFailed')} action={<Button onClick={retry}>{t('common:ui.retry')}</Button>} />
                ))}
            {state === 'loading' && (
                <SkeletonRegion>
                    <Skeleton className="mx-auto h-64 max-w-3xl" />
                </SkeletonRegion>
            )}
            {data && state === 'ready' && (
                <LocationFormBody
                    id={id}
                    {...data}
                    defaultRestaurantId={data.location?.restaurantId ?? requestedRestaurant ?? restaurantId ?? ''}
                    isAdmin={isAdmin}
                />
            )}
        </>
    );
};

export default LocationForm;
